import { LogoutIcon, SwipeLoungeIcon } from "../../assets/Icons";
import { Box, CircularProgress } from "@mui/material";
import { useTheme } from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import {
  CardButtonWrapper,
  CardDetailsWrapper,
  CardHeaderSection,
  CardName,
  CardPlanDescription,
  HeaderNavigation,
  MainSection,
  MainSectionHeading,
  MainSectionSubHeading,
  MainSectionSubHeadingWrapper,
  MainSectionWrapper,
} from "../subscription/style";
import { CustomButton } from "../../globalStyle";
import { CardContainer } from "../../components/dashboardSections/style";
import { useEffect } from "react";
import {
  deleteUserLoaderSelector,
  userDetailsSelector,
} from "../../store/account/selector";
import { AccountSagaActions } from "../../store/account/sagas";
import { useNavigate } from "react-router-dom";
import routeConstant from "../../utils/routeConstant";
import { logoutService } from "../../services/AuthServices";
import { AxiosResponse } from "axios";

const SubscriptionExpiredPage = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const deleteUserLoading = useSelector(deleteUserLoaderSelector);
  const userDetails =
    useSelector(userDetailsSelector) ??
    JSON.parse(localStorage.getItem("User") || "{}");

  useEffect(() => {
    if (userDetails?._id) {
      dispatch(AccountSagaActions.fetchUserDetails(userDetails._id));
    }
  }, [dispatch, userDetails?._id]);

  // ---- Guard Check (same as GuestGuard) ----
  useEffect(() => {
    if (!userDetails?._id) return;

    const now = new Date();
    const planEndDate = userDetails?.parentPlanDetails?.endDate
      ? new Date(userDetails.parentPlanDetails.endDate)
      : null;

    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const planEnd = planEndDate
      ? new Date(
          planEndDate.getFullYear(),
          planEndDate.getMonth(),
          planEndDate.getDate()
        )
      : null;

    const isPlanExpired = planEnd && today > planEnd;
    const isParentFreePlan =
      userDetails?.parentPlanDetails?.planType === "Free";
    const isInvitedUserAccessible =
      userDetails?.parentPlanDetails?.parentDeatils?._id &&
      !userDetails?.isInvitedUserAccesible;

    const isInvitePending = userDetails?.isInvitedBySeat === "PENDING";
    const isInviteAccepted = userDetails?.isInvitedBySeat === "ACCEPTED";
    const hasPlan = Boolean(userDetails?.planId);

    // ✅ If conditions are no longer true → redirect back to dashboard
    if (!isPlanExpired && !isParentFreePlan && !isInvitedUserAccessible) {
      if (hasPlan || isInviteAccepted) {
        navigate(routeConstant.dashboard, { replace: true });
      } else if (isInvitePending) {
        navigate(routeConstant.invitedMember, { replace: true });
      } else {
        navigate(routeConstant.subscription, { replace: true });
      }
    }
  }, [userDetails, navigate]);

  const handleSignUp = () => {
    dispatch(AccountSagaActions.deleteUserDb(userDetails?._id));
  };

  const parent = userDetails?.parentPlanDetails?.parentDeatils || {};
  const isParentFreePlan = userDetails?.parentPlanDetails?.planType === "Free";
  const isInvitedUserAccessible =
    userDetails?.parentPlanDetails?.parentDeatils?._id &&
    !userDetails?.isInvitedUserAccesible;

  // Logout function
  const handleLogout = async () => {
    const response = (await logoutService()) as AxiosResponse;
    if (response?.status === 200) {
      localStorage.clear();
      navigate(`/${routeConstant.login}`, { replace: true });
    }
  };

  return (
    <div style={{ margin: "1.875rem 10rem" }}>
      {/* Top Logo */}
      <HeaderNavigation>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          {/* Left Icon */}
          <SwipeLoungeIcon />

          {/* Logout Button */}
          <CustomButton
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              padding: "0.5rem 1rem",
              borderRadius: "50px",
              background: theme.colors.red ?? "#ff4d4f",
              color: "#fff",
              fontWeight: 500,
              fontSize: "1rem",
              transition: "background 0.3s",
              ":hover": {
                background: theme.colors.redDark ?? "#e04344",
              },
            }}
            onClick={handleLogout}
          >
            {/* Optional Logout Icon */}
            <LogoutIcon  />
            Logout
          </CustomButton>
        </Box>
      </HeaderNavigation>

      {/* Main Section */}
      <MainSection>
        <MainSectionWrapper>
          <MainSectionHeading>
            Your Account Has Been Suspended
          </MainSectionHeading>
        </MainSectionWrapper>
        <MainSectionSubHeadingWrapper>
          <MainSectionSubHeading>
            {isInvitedUserAccessible ? (
              <>
                Your account access has been disabled by{" "}
                <strong>
                  {parent?.fullName} ({parent?.email})
                </strong>
                , who is your primary account holder. Please reach out to them
                for further assistance.
              </>
            ) : isParentFreePlan ? (
              <>
                Your parent account has not made the required payment. Please
                contact{" "}
                <strong>
                  {parent?.fullName} ({parent?.email})
                </strong>{" "}
                (your primary account holder) to complete the payment and regain
                access to your premium features.
              </>
            ) : (
              <>
                Please contact{" "}
                <strong>
                  {parent?.fullName} ({parent?.email})
                </strong>{" "}
                to ensure payment has been made in order to regain access to
                your premium account.
              </>
            )}
          </MainSectionSubHeading>
        </MainSectionSubHeadingWrapper>
      </MainSection>

      {/* Card Container */}
      <CardContainer
        sx={{
          padding: "1.875rem",
          background: theme.colors.richBlack ?? "#081720",
          border: `1px solid ${theme.colors.lightBlue}`,
          backgroundImage: 'url("/assets/images/Ellipse.svg")',
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center center",
          cursor: "default",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          alignItems: "center",
          textAlign: "center",
        }}
      >
        <CardHeaderSection>
          <CardName>Subscription Expired</CardName>
          <CardPlanDescription
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "1rem",
            }}
          >
            {isInvitedUserAccessible ? (
              <p>
                Your access has been disabled by your primary account holder. To
                continue using Swipe Lounge, please contact{" "}
                <strong>
                  {parent?.fullName} ({parent?.email})
                </strong>{" "}
                for reactivation or set up your own subscription.
              </p>
            ) : isParentFreePlan ? (
              <p>
                Since your parent account has not upgraded to a paid plan, your
                access has been restricted. You can either wait until your
                parent account completes the payment or create your own new
                subscription to continue using Swipe Lounge.
              </p>
            ) : (
              <p>
                Alternatively, you can sign up for your own account. Please note
                that your current account and data will be permanently deleted,
                and you’ll need to start fresh with a new account setup.
              </p>
            )}
          </CardPlanDescription>
        </CardHeaderSection>

        <CardDetailsWrapper>
          <Box display="flex" justifyContent="center">
            <CardButtonWrapper style={{ justifyContent: "center" }}>
              <CustomButton
                sx={{
                  padding: "0.813rem 1.25rem",
                  color: theme.colors.white,
                  borderRadius: "100px",
                  border: "1px solid rgba(255, 255, 255, 0.4)",
                  background: theme.colors.backgroundPrimary,
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                }}
                onClick={handleSignUp}
                disabled={deleteUserLoading}
              >
                {deleteUserLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : null}
                Sign Up for My Own Account
              </CustomButton>
            </CardButtonWrapper>
          </Box>
        </CardDetailsWrapper>
      </CardContainer>
    </div>
  );
};

export default SubscriptionExpiredPage;
